import { Component } from '@angular/core'
import { SplashScreen } from '@ionic-native/splash-screen/ngx'
import {
  ModalController,
  NavController,
  NavParams,
  Platform,
} from '@ionic/angular'
import { StatusBar } from '@ionic-native/status-bar/ngx'
import { FCM } from 'cordova-plugin-fcm-with-dependecy-updated/ionic'
import { select } from '@angular-redux/store'
import {
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  Observable,
} from 'rxjs'
import { AppDevice } from './models/app-device.model'
import firebase from 'firebase/compat/app'
import { SideEffectsProvider } from './services/side-effects/side-effects.service'
import { EnvironmentProvider } from './services/environment/environment.service'
import { AppUpdateProvider } from './services/app-update/app-update.service'
import { DeviceProvider } from './services/device/device.service'
import { PageId, resolvePage } from './pages/layout/page-resolver'
import { AddServiceModal } from './components/add-service-modal/add-service-modal.component'
import { HelpersProvider } from './services/helpers/helpers.service'
import { BluetoothLEProvider } from './services/bluetooth-le/bluetooth-le.service'
import { NavDataService } from './services/navigation/navigation.service'
import { Router } from '@angular/router'
import { AngularFireDatabase } from '@angular/fire/compat/database'
import { ScreenOrientation } from '@ionic-native/screen-orientation/ngx'
import { Badge } from '@ionic-native/badge/ngx'
import { Deeplinks } from '@ionic-native/deeplinks/ngx'
import { AddDevicePage } from './pages/add-device/add-device.page'
import { ShareDevicePage } from './pages/share-device/share-device.page'

declare var cordova: any

interface Route {
  method: string
  deviceId?: string
  token?: string
  isDeeplink?: boolean
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  @select('allDevices') allDevices$: Observable<AppDevice[]>
  @select('currentDevice') currentDevice$: Observable<AppDevice>
  @select('currentUser') currentUser$: Observable<firebase.User>

  isMobileDevice: boolean
  currentPageId: PageId = null
  selectedDeviceId: string
  urlRoute: Route = null
  urlParams: { deviceId?: string; token?: string } = {}
  urlData: { data: any } = null
  latestAllDevices: AppDevice[] = [] // Cached devices

  private refreshSettings: () => void

  constructor(
    private platform: Platform,
    private statusBar: StatusBar,
    private viewCtrl: ModalController,
    private helpers: HelpersProvider,
    private device: DeviceProvider,
    private navParams: NavParams,
    private navCtrl: NavController,
    private bluetoothProvider: BluetoothLEProvider,
    private sideEffects: SideEffectsProvider,
    private appUpdateProvider: AppUpdateProvider,
    private splashScreen: SplashScreen,
    private environment: EnvironmentProvider,
    private navData: NavDataService,
    private router: Router,
    private db: AngularFireDatabase,
    private screenOrientation: ScreenOrientation,
    private badge: Badge,
    protected deeplinks: Deeplinks
  ) {
    this.refreshSettings = this.navParams.get('refreshSettings')

    this.setupPlatform()
    this.setupDeviceReadyEvent()
    this.sideEffects.subscribe()

    this.isMobileDevice = this.environment.isNativeApp()
  }

  private setupPlatform(): void {
    this.platform.ready().then(() => {
      if (this.platform.is('android')) {
        this.statusBar.backgroundColorByHexString('#fff')

        const permissions = cordova.plugins.permissions

        const error = (permissionName: string) => {
          console.warn(`${permissionName} permission is not turned on`)
        }

        const grantPermission = (permissionName: string) => {
          permissions.checkPermission(
            permissionName,
            (status) => {
              if (!status.hasPermission) {
                permissions.requestPermission(
                  permissionName,
                  (status) => {
                    if (!status.hasPermission) error(permissionName)
                  },
                  () => error(permissionName)
                )
              }
            },
            null
          )
        }

        // Check and request POST_NOTIFICATIONS permission
        try {
          grantPermission('android.permission.POST_NOTIFICATIONS')
        } catch (e) {
          console.log(`Error requesting permissions: ${e}`)
        }
      }

      this.platform.backButton.subscribe(() => {})

      if (this.platform.is('android') || this.platform.is('ios')) {
        if (this.isTabletOrIpad()) {
          this.screenOrientation.unlock()
        } else {
          this.screenOrientation.lock(
            this.screenOrientation.ORIENTATIONS.PORTRAIT
          )
        }
        this.statusBar.styleDefault()
        this.statusBar.show()

        FCM.onNotification().subscribe(
          (data) => {
            if (data.badge !== undefined) {
              const badgeCount = parseInt(data.badge, 10)
              if (badgeCount === 0) {
                this.badge.clear()
              } else {
                this.badge.set(badgeCount)
              }
            }
          },
          (err) => {
            console.error('Error in FCM subscription:', err)
          }
        )

        this.initializeDeeplinks()
      }

      this.splashScreen.hide()
    })
  }

  private initializeDeeplinks(): void {
    this.deeplinks
          .routeWithNavController(this.navCtrl, {
            '/add-device/:id': AddDevicePage,
            '/share-device/:token': ShareDevicePage,
            '/add-satellite-service': AddServiceModal,
          })
          .subscribe({
            next: async (match) => {
              console.log('[Deeplink matched]', match)

              const route = match.$route
              const args = match.$args

              if (route === '/add-device/:id') {
                await this.handleAddDeviceRoute(args.id, this.latestAllDevices.length)
              } else if (route === '/share-device/:token') {
                await this.handleShareDeviceRoute(this.latestAllDevices.length)
              } else if (route === '/add-satellite-service') {
                this.openAddServiceModal()
              }
            },
            error: (noMatch) => {
              console.warn('[Deeplink no match]', noMatch)
            },
          })
        }

  private setupDeviceReadyEvent(): void {
    document.addEventListener(
      'deviceready',
      () => {
        document.addEventListener(
          'resume',
          () => {
            this.onAppResume()
          },
          false
        )
      },
      false
    )
  }

  private onAppResume(): void {
    // The app has come to the foreground. Reconnect the database.
    this.db.database.goOffline()
    this.db.database.goOnline()
  }

  async ngOnInit() {
    // Get route params from ActivatedRoute
    // get data from url and window state
    this.urlRoute = getRoute()
    if (this.urlRoute && this.urlRoute.deviceId) {
      this.navData.set('deviceId', this.urlRoute.deviceId)
    }
    if (this.urlRoute && this.urlRoute.token) {
      this.navData.set('token', this.urlRoute.token)
    }
    this.urlData = window.history.state

    // Navigate to the 'loading' page
    await this.router.navigate(['loading'])

    this.subscribeToLayoutState()
  }

  exit(): void {
    this.viewCtrl.dismiss()
  }

  isTabletOrIpad(): boolean {
    return this.platform.width() >= 500
  }

  openAddServiceModal = () => {
    this.helpers.showModal(
      AddServiceModal,
      {
        refreshSettings: this.refreshSettings,
        serviceType: 'satellite',
        newDeviceId: this.urlRoute.deviceId,
      },
      false,
      false,
      'setup-modal-container'
    )
  }

  isMateLinked() {
    return this.device.isMateLinked()
  }

  async connectToBluetooth() {
    if (this.isMateLinked() && this.isMobileDevice) {
      const mateId = this.device.currentBRNKLandMateId$.value.mateId
      if (!this.bluetoothProvider.connected.value) {
        await this.bluetoothProvider.connectToMate(mateId)
      }
    }
  }

  subscribeToLayoutState() {
    combineLatest([
      this.allDevices$,
      this.currentUser$,
      this.currentDevice$,
      this.device.currentBRNKLandMateId$,
      this.appUpdateProvider.mustUpdate$,
    ])
      .pipe(
        debounceTime(750),
        distinctUntilChanged((prev, curr) => {
          const [
            prevAllDevices,
            prevUser,
            prevDevice,
            prevIds,
            prevMustUpdate,
          ] = prev
          const [
            currAllDevices,
            currUser,
            currDevice,
            currIds,
            currMustUpdate,
          ] = curr
          // only update if the number of devices changes
          const sameDeviceCount =
            (prevAllDevices ?? []).length === (currAllDevices ?? []).length
          // or any of the other values change
          const sameUser = prevUser === currUser
          const sameDevice = prevDevice === currDevice
          const sameIds = prevIds.deviceId === currIds.deviceId
          const sameMustUpdate = prevMustUpdate === currMustUpdate

          return (
            sameDeviceCount &&
            sameUser &&
            sameDevice &&
            sameIds &&
            sameMustUpdate
          )
        })
      )
      .subscribe(async (vals) => {
        const [allDevices, user, device, ids, mustUpdate] = vals

        const isAddDeviceRoute =
          this.urlRoute && this.urlRoute.method.includes('add-device')

        const isShareDeviceRoute =
          this.urlRoute && this.urlRoute.method.includes('share-device')

        const isAddServiceRoute =
          this.urlRoute &&
          this.urlRoute.method.includes('add-satellite-service')


        console.log( this.urlRoute)
        const pageId = resolvePage(
          allDevices,
          user,
          device,
          ids.deviceId,
          isAddDeviceRoute,
          isShareDeviceRoute,
          isAddServiceRoute,
          mustUpdate
        )

        if (this.currentPageId === pageId) {
          return
        }

        // redirect through deeplink to mobile app
        if (this.urlRoute.isDeeplink && this.isMobileDevice) {
          console.log(
            'Deeplink detected, redirecting to mobile app...'
          )
          // If the app is opened through a deeplink, redirect to the app
          // check method and direct accordingly
          let route = 'brnklapp://app'
          switch (pageId) {
            case PageId.AddDevice:
              route += '/add-device'
              break
            case PageId.ShareDevice:
              route += '/share-device'
              break
            case PageId.AddService:
              route += '/add-satellite-service'
              break
            default:
              return
          }
          console.log(`Redirecting to: ${route}`)
          window.location.href = route
        }

        if (pageId === PageId.AddService) {
          await this.setRoot(PageId.SelectDevice).then(() => {
            delay(2000)
            this.openAddServiceModal()
          })
        }

        if (isAddDeviceRoute && pageId === PageId.AddDevice) {
          const nDevices = allDevices ? allDevices.length : -1

          await this.handleAddDeviceRoute(ids.deviceId, nDevices)
        } else if (isShareDeviceRoute && pageId === PageId.ShareDevice) {
          const nDevices = allDevices ? allDevices.length : -1
          await this.handleShareDeviceRoute(nDevices)
        } else if (pageId === PageId.SetupWizard) {
          await this.setPages([
            { pageId: PageId.DeviceStats },
            { pageId: PageId.SetupWizard },
          ])
        } else {
          await this.setRoot(pageId)
          // silently connects BRNKL-Mate bluetooth in background
          await this.connectToBluetooth()
        }
      })
  }

  private async setRoot(pageId: PageId, params?: any) {
    if (this.currentPageId !== pageId) {
      const page = this.pageIdToPage(pageId)
      await this.router.navigate([page], {
        queryParams: params,
        replaceUrl: true,
      })
      this.currentPageId = pageId
    }
  }

  private async setPages(
    pagesById: Array<{
      pageId: PageId
      params?: any
    }>
  ) {
    const topPageId = pagesById[pagesById.length - 1].pageId

    if (topPageId !== this.currentPageId) {
      const pages = pagesById.map((pageById) => {
        return {
          page: this.pageIdToPage(pageById.pageId),
          params: pageById.params,
        }
      })
      // TODO: fix how to set pages with angular routing
      await this.navCtrl.navigateForward(pages)
      this.currentPageId = topPageId
    }
  }

  private async handleAddDeviceRoute(deviceId: string, nDevices: number) {
    // To avoid switching to the tabs page when a BRNKL pushes data
    // ensure the deviceId is set to null. Then load the select device page
    console.log("PARAMS", this.urlParams)
    if (deviceId != null) {
      this.device.setCurrentDeviceId(null)
      return
    }
    if (nDevices > 0) {
      await this.setRoot(PageId.SelectDevice, this.urlParams)

      await this.navCtrl.navigateForward(this.pageIdToPage(PageId.AddDevice))
      this.currentPageId = PageId.AddDevice
    } else {
      // Must use setRoot() to properly pass params to Add-Device page
      await this.setRoot(PageId.AddDevice, this.urlParams)
    }

    // Set undefined as we only want to navigate to the add device page once
    this.urlRoute = undefined
  }

  private async handleShareDeviceRoute(nDevices: number) {
    // To avoid switching to the tabs page when a BRNKL pushes data
    // ensure the deviceId is set to null. Then load the select device pag
    if (nDevices > 0) {
      await this.setRoot(PageId.SelectDevice, this.urlParams)

      await this.navCtrl.navigateForward(this.pageIdToPage(PageId.ShareDevice))
      this.currentPageId = PageId.ShareDevice
    } else {
      // Must use setRoot() to properly pass params to Share-Device page
      await this.setRoot(PageId.ShareDevice, this.urlParams)
    }

    // Set undefined as we only want to navigate to the add device page once
    this.urlRoute = undefined
  }

  private pageIdToPage(pageId: PageId): string {
    if (pageId === PageId.DeviceStats) {
      return 'tabs'
    } else if (pageId === PageId.SelectDevice) {
      return 'select-device'
    } else if (pageId === PageId.UserLogin) {
      return 'login'
    } else if (pageId === PageId.AddDevice) {
      return 'add-device'
    } else if (pageId === PageId.ShareDevice) {
      return 'share-device'
    } else if (pageId === PageId.ForceUpdate) {
      return 'force-update'
    } else {
      return 'loading'
    }
  }
}

const getRoute = (): Route | undefined => {
  try {
    const { href } = window.location
    const url = new URL(href)
    const pathname = url.pathname // e.g. "/add-device/12345" or "/deeplink/add-device/12345"
    const parts = pathname.split('/').filter(Boolean) // removes empty entries

    const supportedMethods = [
      'add-device',
      'share-device',
      'add-satellite-service',
      'invite',
    ]

    // Look for the first supported method in the path
    const methodIndex = parts.findIndex((part) =>
      supportedMethods.includes(part)
    )

    const isDeeplink = parts.includes('deeplink')

    if (methodIndex === -1) return undefined

    const method = parts[methodIndex]
    const param = parts[methodIndex + 1]

    if (method === 'share-device') {
      return { method, token: param , isDeeplink}
    }
    console.log('Parsed URL:', { method, deviceId: param , isDeeplink})
    return { method, deviceId: param, isDeeplink  }
  } catch (err){
    console.log('Error parsing URL:', err)
    return undefined
  }
}
